/* eslint-disable no-multi-assign */
/* eslint-disable react/sort-comp */
/* eslint-disable prefer-spread */
/* eslint-disable no-empty */
/* eslint-disable max-lines */
import React, { Component } from 'react';
import Epub from 'epubjs/lib/index';
import { Rendition, Book } from 'epubjs';
import styled from '@emotion/styled';
import { themes } from '@cont/EpubNotes/utils';
import { EpubViewProps } from '../types';

interface IHighlight {
  cfi: string;
}

interface EpubViewState {
  prevSearchPosition: string;
  isLoaded: boolean;
  isSearchLoading: boolean;
  searchHighlights: IHighlight[];
  toc: any[];
}

interface LoadingProps {
  $loading: boolean;
}

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  .epub-container {
    user-select: none;
  }
`;

const InnerWrapper = styled.div<LoadingProps>`
  height: 100%;
  opacity: ${({ $loading }) => ($loading ? '0' : '1')};
`;

const SearchLoader = styled.div<LoadingProps>`
  opacity: ${({ $loading }) => ($loading ? '1' : '0')};
`;

const HIGHLIGHT_COLOR = '#FFF000';
const NAVIGATION_HIGHLIGHT_COLOR = '#ff8d03';
const DARK_THEME_FONT_COLOR = '#F4F4F3';
const LIGHT_THEME_FONT_COLOR = '#3C4852';
const searchExcludedKeywords = ['cvi', 'Title', 'title', 'part', 'toc'];
const MAX_SEARCH_RESULT = 1000;

class EpubView extends Component<EpubViewProps, EpubViewState> {
  public static defaultProps = {
    currentSearchPosition: 1,
    searchTerm: '',
    loadingView: null,
    locationChanged: null,
    tocChanged: null,
    epubOptions: {},
    epubInitOptions: {},
    theme: {
      color: themes.LIGHT,
      fontSize: 100
    }
  };

  location: EpubViewProps['location'];

  book: Book;

  rendition: Rendition;

  prevPage: () => void;

  nextPage: () => void;

  viewerRef: React.RefObject<any>;

  timerRef: any;

  constructor(props) {
    super(props);
    this.state = {
      prevSearchPosition: '',
      searchHighlights: [],
      isLoaded: false,
      isSearchLoading: false,
      toc: []
    };
    this.viewerRef = React.createRef();
    this.location = props.location;
    this.book = this.rendition = this.prevPage = this.nextPage = null;
    this.timerRef = null;
  }

  componentDidMount() {
    const { theme } = this.props;
    this.initBook();
    document.addEventListener('keyup', this.handleKeyPress, false);
  }

  resetBook(navigateToTop) {
    this.setState(
      {
        isLoaded: false
      },
      () => {
        this.initBook(navigateToTop);
      }
    );
  }

  isCFIValid = (range) => {
    return (
      range.startContainer.parentElement.tagName !== 'td' &&
      range.startContainer.parentElement.className !== 'chapterName' &&
      range.startContainer.parentElement.tagName !== 'title'
    );
  };

  initBook(navigateToTop?: boolean) {
    const { url, tocChanged, epubInitOptions, searchTerm } = this.props;

    if (this.book) {
      this.book.destroy();
    }
    this.book = new Epub(url, epubInitOptions);
    this.book.loaded.navigation.then(({ toc }) => {
      this.setState(
        {
          isLoaded: true,
          toc
        },
        () => {
          // @ts-ignore
          if (tocChanged) tocChanged(toc);
          this.initReader(navigateToTop);
        }
      );
    });
  }

  shouldComponentUpdate(nextProps, prevState) {
    const { isLoaded } = this.state;
    const { location, resize, searchTerm, currentSearchPosition, theme, url } =
      this.props;
    const { isSearchLoading } = this.state;

    return (
      !isLoaded ||
      nextProps.url !== url ||
      nextProps.location !== location ||
      nextProps.resize !== resize ||
      nextProps.searchTerm !== searchTerm ||
      nextProps.currentSearchPosition !== currentSearchPosition ||
      nextProps.theme !== theme ||
      prevState.isSearchLoading !== isSearchLoading
    );
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      location,
      url,
      resize,
      searchTerm,
      currentSearchPosition,
      theme,
      onDisableNavigation
    } = this.props;

    const { isLoaded, isSearchLoading } = this.state;

    if (prevProps.location !== location && this.location !== location) {
      this.rendition?.display(location as number);
    }
    if (prevProps.url !== url || prevProps.resize !== resize) {
      this.resetBook(false);
    }
    if (prevProps.searchTerm !== searchTerm) {
      this.onSearchChange(searchTerm, true);
    }

    if (
      prevProps.currentSearchPosition !== currentSearchPosition &&
      prevProps.searchTerm === searchTerm
    ) {
      this.navigate(currentSearchPosition);
    }

    if (
      prevProps.theme?.color !== theme.color ||
      prevProps.theme?.fontSize !== theme.fontSize
    ) {
      const isColorChanged = prevProps.theme?.color !== theme.color;
      this.changeTheme(isColorChanged);
    }

    if (
      prevState.isSearchLoading !== isSearchLoading ||
      prevState.isLoaded !== isLoaded
    ) {
      onDisableNavigation(isSearchLoading || !isLoaded);
    }
  }

  componentWillUnmount() {
    this.book = this.rendition = this.prevPage = this.nextPage = null;
    document.removeEventListener('keyup', this.handleKeyPress, false);
    clearTimeout(this.timerRef);
  }

  initReader(navigateToTop?: boolean) {
    const { toc, prevSearchPosition } = this.state;
    const { location, epubOptions, getRendition, searchTerm, theme } =
      this.props;
    const updatedLocation = navigateToTop ? 0 : location;
    const node = this.viewerRef.current;
    this.rendition = this.book.renderTo(node, {
      // @ts-ignore
      contained: true,
      width: '100%',
      height: '100%',
      flow: 'scrolled-doc',
      ...epubOptions
    });

    this.prevPage = () => {
      this.rendition.prev();
    };
    this.nextPage = () => {
      this.rendition.next();
    };
    this.registerEvents();
    this.registerTheme();
    this.addMathJAX();

    if (getRendition) getRendition(this.rendition);

    if (
      typeof updatedLocation === 'string' ||
      typeof updatedLocation === 'number'
    ) {
      this.rendition.display(updatedLocation as number);
    } else if (toc.length > 0 && toc[0].href) {
      this.rendition.display(toc[0].href);
    } else {
      this.rendition.display();
    }

    // Apply theme on book initialization
    if (theme?.color) {
      this.rendition.themes.select(theme?.color);
    } else {
      this.rendition.themes.select(themes.LIGHT);
    }

    // Apply font size on book initialization
    if (theme?.fontSize) {
      this.renderFontSize();
    }

    if (searchTerm) {
      this.onSearchChange(searchTerm, false);
    }
  }

  renderFontSize() {
    const { theme } = this.props;
    const { fontSize } = theme || {};

    this.rendition.themes.fontSize(`${fontSize}%`);
    this.rendition.views().forEach((view) => {
      // @ts-ignore
      if (view.pane) view.pane.render();
    });
  }

  registerTheme() {
    this.rendition.themes.register({
      [themes.DARK]: {
        body: {
          'background-color': '#2A2B2D',
          '-webkit-filter': 'invert(1) hue-rotate(180deg)',
          filter: 'invert(1) hue-rotate(180deg)'
        },
        img: {
          '-webkit-filter': 'invert(1) hue-rotate(180deg)',
          filter: 'invert(1) hue-rotate(180deg)'
        }
      },
      [themes.LIGHT]: {
        body: {
          'background-color': '#FFFFFF',
          color: LIGHT_THEME_FONT_COLOR
        },
        p: { color: LIGHT_THEME_FONT_COLOR },
        li: { color: LIGHT_THEME_FONT_COLOR },
        ul: { color: LIGHT_THEME_FONT_COLOR }
      }
    });
  }

  isEndOfBook() {
    return this.rendition.location.atEnd;
  }

  isStartOfBook() {
    return this.rendition.location.atStart;
  }

  getRange = (cfi) => {
    return this.book.getRange(cfi);
  };

  registerEvents() {
    const { handleKeyPress, handleTextSelected } = this.props;
    this.rendition.on('locationChanged', this.onLocationChange);
    this.rendition.on('keyup', handleKeyPress || this.handleKeyPress);

    // Image Functionality is switched off temporarily
    // this.rendition.on('rendered', this.onHandleImage);
    if (handleTextSelected) {
      this.rendition.on('selected', handleTextSelected);
    }
  }

  addMathJAX() {
    this.rendition.hooks.content.register((content) => {
      const section = this.book.section(content.sectionIndex);
      const mathml = section.properties.includes('mathml');

      if (mathml) {
        return content.addScript(
          'https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.4/MathJax.js?config=TeX-MML-AM_CHTML'
        );
      }
    });
  }

  changeTheme(isColorChanged) {
    // This is a hack because switching between themes is not smooth
    // Refer https://github.com/futurepress/epub.js/issues/1208
    if (isColorChanged) {
      this.setState({ isSearchLoading: true });
      this.resetBook(false);
      this.timerRef = setTimeout(() => {
        this.setState({ isSearchLoading: false });
      }, 500);
    } else {
      this.renderFontSize();
    }
  }

  addHighlightAnnotation = (cfi, color, toggleSearchLoading?: boolean) => {
    this.book.getRange(cfi).then((range) => {
      if (toggleSearchLoading) this.setState({ isSearchLoading: false });

      if (this.isCFIValid(range)) {
        try {
          this.rendition.annotations.add('highlight', cfi, {}, () => {}, '', {
            fill: color,
            'fill-opacity': '0.5',
            'mix-blend-mode': 'unset'
          });
        } catch {}
      }
    });
  };

  changeHighlightAnnotation = (cfi, color) => {
    this.rendition.annotations.remove(cfi, 'highlight');
    this.addHighlightAnnotation(cfi, color);
  };

  navigate = (searchPostion, shouldHighlight: boolean = true) => {
    const { searchHighlights, prevSearchPosition } = this.state;
    const { onChangeSearchPosition } = this.props;

    // Remove highlight from previous navigated text
    if (prevSearchPosition)
      this.changeHighlightAnnotation(prevSearchPosition, HIGHLIGHT_COLOR);

    if (searchHighlights.length) {
      const element = searchHighlights[searchPostion - 1].cfi;

      // Highlight current navigated text
      if (searchPostion >= 1 && shouldHighlight)
        this.changeHighlightAnnotation(element, NAVIGATION_HIGHLIGHT_COLOR);

      // Navigation to current text

      try {
        this.rendition.display(element).then(() => {
          this.setState({ isSearchLoading: false });
        });
      } catch {}

      this.setState({
        prevSearchPosition: element
      });
    }
  };

  searchWithinBook = (searchTerm) => {
    // @ts-ignore
    const filteredSpineItems = this.book.spine.spineItems.filter((item) => {
      const searchExcludedWords = searchExcludedKeywords.filter((word) =>
        item.idref.includes(word)
      );
      return !item.idref.includes(searchExcludedWords[0]);
    });

    return Promise.all(
      filteredSpineItems.map((item) =>
        item
          .load(this.book.load.bind(this.book))
          .then(item.find.bind(item, searchTerm))
          .finally(item.unload.bind(item))
      )
    ).then((results) => Promise.resolve([].concat.apply([], results)));
  };

  getFilteredArray = async (slicedResults) => {
    const results = slicedResults.map(async (result) => {
      const range = await this.book.getRange(result.cfi);
      return { ...result, range };
    });
    return Promise.all(results);
  };

  onSearchChange = (searchTerm, shouldNavigate) => {
    const { onChangeSearchResults, currentSearchPosition } = this.props;
    const { searchHighlights, prevSearchPosition } = this.state;

    // Remove Previous Highlight
    searchHighlights.forEach((highlight) => {
      this.rendition.annotations.remove(highlight.cfi, 'highlight');
    });
    if (prevSearchPosition)
      this.rendition.annotations.remove(prevSearchPosition, 'highlight');

    // Add New Highlight
    if (searchTerm) {
      this.searchWithinBook(searchTerm).then(async (results) => {
        const updatedLength = Math.min(MAX_SEARCH_RESULT, results.length);
        const slicedResults = results.slice(0, updatedLength);
        // this gives an array of results with range object attached to it
        const slicedResultsWithRange = await this.getFilteredArray(
          slicedResults
        );
        // the map is to remove range object from each item
        const updatedResults = slicedResultsWithRange
          .filter((result) => this.isCFIValid(result.range))
          .map(({ range, ...rest }) => ({ ...rest }));

        if (updatedResults.length <= MAX_SEARCH_RESULT)
          this.setState({ isSearchLoading: updatedResults.length > 0 });

        if (onChangeSearchResults) onChangeSearchResults(updatedResults.length);

        updatedResults.slice(0, MAX_SEARCH_RESULT).forEach((r, i) => {
          // This happens when theme is changed and we want to persist current displayed word
          const isCurrentPosition = i + 1 === currentSearchPosition;

          this.addHighlightAnnotation(
            r.cfi,
            isCurrentPosition ? NAVIGATION_HIGHLIGHT_COLOR : HIGHLIGHT_COLOR,
            !shouldNavigate
          );
        });

        this.setState(
          {
            searchHighlights: updatedResults,
            prevSearchPosition: ''
          },
          () => {
            if (shouldNavigate) this.navigate(1, false);
          }
        );
      });
    } else {
      this.setState({
        searchHighlights: [],
        prevSearchPosition: ''
      });
    }
  };

  onHandleImage = (_e, i) => {
    const { handleImageClick } = this.props;
    i.document.documentElement.addEventListener('click', (e) => {
      const imageSrc = e.target.src;
      const imageHeight = e.target.height;
      const imageWidth = e.target.width;
      if (handleImageClick) handleImageClick(imageSrc, imageHeight, imageWidth);
    });
  };

  onLocationChange = (loc) => {
    const { locationChanged, onNavigation } = this.props;
    const newLocation = loc && loc.start;

    this.location = newLocation;
    const isEndOfBook = this.isEndOfBook();
    const isStartOfBook = this.isStartOfBook();
    const locations = this.rendition.location;

    const currentChapter =
      // @ts-ignore
      this.rendition.book.spine.items[locations.start.index]?.idref;
    const pageNoInChapter = locations.start?.displayed?.page;

    if (locationChanged) locationChanged(newLocation);

    if (onNavigation)
      onNavigation({
        isEndOfBook,
        isStartOfBook,
        currentChapter,
        pageNoInChapter,
        direction: ''
      });
  };

  renderBook() {
    const { loadingView } = this.props;
    const { isSearchLoading } = this.state;

    return (
      <>
        <InnerWrapper ref={this.viewerRef} $loading={isSearchLoading} />
        <SearchLoader $loading={isSearchLoading}>{loadingView}</SearchLoader>
      </>
    );
  }

  handleKeyPress = (e) => {
    if (e.target.name === 'Epub-Search') return;
    const { key } = e;
    if (key && key === 'ArrowRight') this.nextPage();
    if (key && key === 'ArrowLeft') this.prevPage();
  };

  render() {
    const { isLoaded } = this.state;
    const { loadingView } = this.props;

    return <Wrapper>{(isLoaded && this.renderBook()) || loadingView}</Wrapper>;
  }
}

export default EpubView;
